/**
 * Notification Bus
 * Simple event-based notification system for displaying toast messages
 *
 * Usage:
 *   import { NotificationBus } from './services/notification-bus.js';
 *   NotificationBus.error('Something went wrong');
 *   NotificationBus.success('Action completed successfully');
 */

const NOTIFICATION_EVENT = 'app:notify';

/**
 * @typedef {Object} NotificationDetail
 * @property {string} message - The notification message to display
 * @property {'error'|'success'|'warning'|'info'} type - The notification type
 * @property {number} duration - How long to show the notification (ms)
 * @property {number} id - Unique identifier for this notification
 * @property {string} [source] - Optional source identifier for debugging
 */

/**
 * Dispatch a notification event
 * @param {string} message - The message to display
 * @param {'error'|'success'|'warning'|'info'} type - The notification type
 * @param {number} duration - Duration in milliseconds
 * @param {string} [source] - Optional source for debugging
 */
function show(message, type = 'info', duration = 5000, source = undefined) {
    const detail = {
        message,
        type,
        duration,
        id: Date.now() + Math.random(), // Ensure uniqueness for rapid calls
        source,
    };

    window.dispatchEvent(new CustomEvent(NOTIFICATION_EVENT, { detail }));
}

/**
 * NotificationBus API
 * Provides convenience methods for different notification types
 */
export const NotificationBus = {
    /**
     * Show a generic notification
     */
    show,

    /**
     * Show an error notification (longer duration)
     * @param {string} message - Error message
     * @param {string} [source] - Optional source identifier
     */
    error(message, source) {
        show(message, 'error', 8000, source);
    },

    /**
     * Show a success notification (shorter duration)
     * @param {string} message - Success message
     * @param {string} [source] - Optional source identifier
     */
    success(message, source) {
        show(message, 'success', 4000, source);
    },

    /**
     * Show a warning notification
     * @param {string} message - Warning message
     * @param {string} [source] - Optional source identifier
     */
    warning(message, source) {
        show(message, 'warning', 6000, source);
    },

    /**
     * Show an info notification
     * @param {string} message - Info message
     * @param {string} [source] - Optional source identifier
     */
    info(message, source) {
        show(message, 'info', 5000, source);
    },

    /**
     * Show a persistent notification (no auto-dismiss)
     * User must manually close it
     * @param {string} message - Notification message
     * @param {'error'|'success'|'warning'|'info'} type - Notification type
     * @param {string} [source] - Optional source identifier
     */
    persistent(message, type = 'info', source) {
        show(message, type, 0, source);
    },

    /**
     * Event name constant for external listeners
     */
    EVENT_NAME: NOTIFICATION_EVENT,
};
